import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
  Badge,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import Swal from 'sweetalert2';
import SignatureModal from '../../components/SignatureModal';
import RecipientesManager from '../../components/RecipientesManager';
import { generateAforoPDF, openAforoPDF } from '../../components/AforoPDF';
import { postVisitaAforo, getRecipientesList } from '../../helpers/backend';

// Función para obtener URL base del backend
const getBaseUrl = () => {
  const URL = process.env.REACT_APP_API_URL;
  const url = URL.split("/api");
  return url[0];
};

const AforoForm = ({ caso, onSuccess }) => {
  // Función para convertir fecha_informe del formato "01/09/2025 12:00 AM" a "2025-09-01"
  const convertirFechaInforme = (fechaInforme) => {
    if (!fechaInforme) return null;
    
    try {
      // Extraer solo la parte de la fecha (antes del espacio)
      const fechaParte = fechaInforme.split(' ')[0];
      // Dividir por "/" para obtener [día, mes, año]
      const [dia, mes, año] = fechaParte.split('/');
      
      // Construir directamente el string YYYY-MM-DD para evitar problemas de zona horaria
      const diaFormateado = dia.padStart(2, '0');
      const mesFormateado = mes.padStart(2, '0');
      
      // Validar que los valores sean válidos
      if (año && mes && dia && 
          parseInt(mes) >= 1 && parseInt(mes) <= 12 && 
          parseInt(dia) >= 1 && parseInt(dia) <= 31) {
        return `${año}-${mesFormateado}-${diaFormateado}`;
      }
      
      return null;
    } catch (error) {
      console.error('Error al convertir fecha_informe:', error);
      return null;
    }
  };

  // Función para obtener la fecha inicial (fecha del informe o fecha actual como fallback)
  const obtenerFechaInicial = () => {
    const fechaInforme = convertirFechaInforme(caso?.informe?.fecha_informe);
    if (fechaInforme) {
      return fechaInforme;
    }
    
    // Fallback a fecha actual si no hay fecha_informe
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  // Función para obtener el día de la semana actual
  const obtenerDiaSemanaActual = () => {
    const diasSemanaMap = {
      0: 'domingo',
      1: 'lunes', 
      2: 'martes',
      3: 'miercoles',
      4: 'jueves',
      5: 'viernes',
      6: 'sabado'
    };
    const now = new Date();
    return diasSemanaMap[now.getDay()];
  };

  // Función para obtener el día de la semana basado en una fecha específica
  const obtenerDiaSemanaDeFecha = (fecha) => {
    const diasSemanaMap = {
      0: 'domingo',
      1: 'lunes', 
      2: 'martes',
      3: 'miercoles',
      4: 'jueves',
      5: 'viernes',
      6: 'sabado'
    };
    
    if (!fecha) return obtenerDiaSemanaActual();
    
    try {
      const fechaObj = new Date(fecha + 'T00:00:00');
      return diasSemanaMap[fechaObj.getDay()];
    } catch (error) {
      console.error('Error al obtener día de la semana de la fecha:', error);
      return obtenerDiaSemanaActual();
    }
  };

  // Función para obtener el día de la semana inicial (basado en fecha del informe)
  const obtenerDiaSemanaInicial = () => {
    const fechaInicial = obtenerFechaInicial();
    return obtenerDiaSemanaDeFecha(fechaInicial);
  };

  // Función para obtener la hora actual
  const obtenerHoraActual = () => {
    const now = new Date();
    return now.toTimeString().slice(0, 5);
  };

  // Función para agrupar visitas por semanas dinámicas usando total_frecuencia y total_semana
  const agruparVisitasPorSemanasDinamicas = (visitasParaAgrupar) => {
    if (!visitasParaAgrupar || visitasParaAgrupar.length === 0) return [];
    
    // Obtener configuración del aforo
    const totalFrecuencia = caso?.aforos?.[0]?.total_frecuencia || 7;
    const totalSemana = caso?.aforos?.[0]?.total_semana || 1;
    
    // Ordenar visitas por fecha ascendente
    const visitasOrdenadas = [...visitasParaAgrupar]
      .filter(visita => visita.fecha_visita)
      .sort((a, b) => new Date(a.fecha_visita) - new Date(b.fecha_visita));
    
    const semanas = [];
    let visitaIndex = 0;
    
    // Crear semanas dinámicas basadas en total_semana
    for (let numeroSemana = 1; numeroSemana <= totalSemana && visitaIndex < visitasOrdenadas.length; numeroSemana++) {
      const visitasSemana = [];
      
      // Tomar hasta total_frecuencia visitas para esta semana
      for (let i = 0; i < totalFrecuencia && visitaIndex < visitasOrdenadas.length; i++) {
        visitasSemana.push(visitasOrdenadas[visitaIndex]);
        visitaIndex++;
      }
      
      if (visitasSemana.length > 0) {
        semanas.push({
          numeroSemana,
          visitas: visitasSemana,
          fechaInicio: visitasSemana[0].fecha_visita,
          fechaFin: visitasSemana[visitasSemana.length - 1].fecha_visita
        });
      }
    }
    
    return semanas;
  };

  // Función para validar límites de visitas según total_frecuencia y total_semana
  const validarLimiteVisitas = () => {
    const totalFrecuencia = caso?.aforos?.[0]?.total_frecuencia || 7;
    const totalSemana = caso?.aforos?.[0]?.total_semana || 1;
    const maxVisitas = totalFrecuencia * totalSemana;
    
    return {
      maxVisitas,
      visitasActuales: visitas.length,
      visitasRestantes: Math.max(0, maxVisitas - visitas.length),
      puedeAgregarMas: visitas.length < maxVisitas,
      totalFrecuencia,
      totalSemana
    };
  };

  // Función para validar que no existan visitas duplicadas en la misma fecha
  const validarVisitaDuplicada = (fechaVisita, diaSemanaNuevo, visitaActualId = null) => {
    // Obtener el día de la semana de la fecha seleccionada
    const fechaObj = new Date(fechaVisita + 'T00:00:00');
    const diasSemanaMap = {
      0: 'domingo',
      1: 'lunes', 
      2: 'martes',
      3: 'miercoles',
      4: 'jueves',
      5: 'viernes',
      6: 'sabado'
    };
    const diaSemanaDeFecha = diasSemanaMap[fechaObj.getDay()];
    
    // Verificar si el día de la semana coincide con la fecha
    if (diaSemanaNuevo && diaSemanaNuevo !== diaSemanaDeFecha) {
      return {
        esValida: false,
        mensaje: `El día de la semana seleccionado (${diaSemanaNuevo}) no coincide con la fecha ${fechaVisita} que corresponde a ${diaSemanaDeFecha}`
      };
    }
    
    // Verificar duplicados en otras visitas
    const visitasDuplicadas = visitas.filter(visita => {
      // Excluir la visita actual si se está editando
      if (visitaActualId && visita.id === visitaActualId) {
        return false;
      }
      
      return visita.fecha_visita === fechaVisita;
    });
    
    if (visitasDuplicadas.length > 0) {
      return {
        esValida: false,
        mensaje: `Ya existe una visita programada para la fecha ${fechaVisita}. Solo se permite una visita por día.`
      };
    }
    
    // Validar límites de visitas
    const limites = validarLimiteVisitas();
    if (!limites.puedeAgregarMas && !visitaActualId) {
      return {
        esValida: false,
        mensaje: `Se ha alcanzado el límite máximo de ${limites.maxVisitas} visitas (${limites.totalFrecuencia} frecuencias × ${limites.totalSemana} semanas).`
      };
    }
    
    return { esValida: true };
  };
  const [activeTab, setActiveTab] = useState('recipientes');
  const [visitas, setVisitas] = useState([]);
  const [visitaActualIndex, setVisitaActualIndex] = useState(0);
  const [nextVisitaId, setNextVisitaId] = useState(1);
  const [hoveredVisitIndex, setHoveredVisitIndex] = useState(null);

  // Estados para los modales de firma
  const [showRepresentanteSignature, setShowRepresentanteSignature] = useState(false);
  
  // Estados para firmas temporales
  const [tempRepresentanteFirma, setTempRepresentanteFirma] = useState(null);

  // Verificar si el caso está finalizado
  const isCasoFinalizado = caso?.estado === 'FINALIZADO';

  // Estado para opciones de recipientes
  const [opcionesRecipientes, setOpcionesRecipientes] = useState([]);

  // Cargar opciones de recipientes desde la API
  useEffect(() => {
    const cargarRecipientes = async () => {
      try {
        const response = await getRecipientesList();
        if (response && response.data && response.data.results) {
          const opcionesFormateadas = response.data.results.map(recipiente => ({
            value: recipiente.id,
            label: `${recipiente.nombre} - ${recipiente.descripcion}`
          }));
          setOpcionesRecipientes(opcionesFormateadas);
        }
      } catch (error) {
        console.error('Error al cargar recipientes:', error);
        // Mantener opciones por defecto en caso de error
        setOpcionesRecipientes([
          { value: 'BD', label: 'BD - BOLSA DOMESTICA 50*75 CM' },
          { value: 'BS', label: 'BS - BOLSA SEMIINDUSTRIAL 60*86 CM' },
          { value: 'BI', label: 'BI - BOLSA INDUSTRIAL 70*120 CM' }
        ]);
      }
    };
    
    cargarRecipientes();
  }, []);



  // Función para crear una visita vacía
  const crearVisitaVacia = (id = 1) => {
    return {
      id,
      aforo: id,
      fecha_visita: obtenerFechaInicial(), // Usar fecha del informe en lugar de fecha actual
      hora_visita: obtenerHoraActual(),
      dia_semana: obtenerDiaSemanaInicial(), // Usar día de la semana basado en la fecha del informe
      observaciones: '',
      nombre_representante: '',
      firma_representante: null,
      recipientes: [], // Nuevo formato: array de recipientes
      // Control de estado
      esNueva: true,
      servidorId: null
    };
  };

  // Estado para la visita actual
  const [visitaActual, setVisitaActual] = useState(() => crearVisitaVacia(1));

  // Inicializar visitas
  useEffect(() => {
    const primeraVisita = crearVisitaVacia(1);
    setVisitas([primeraVisita]);
    setVisitaActual(primeraVisita);
  }, []);

  // Cargar datos existentes del aforo si existen
  useEffect(() => {
    if (caso?.aforos && caso.aforos.length > 0) {
      const aforoExistente = caso.aforos[0];
      if (aforoExistente.visitas && aforoExistente.visitas.length > 0) {
        const visitasExistentes = aforoExistente.visitas.map((visita, index) => ({
          ...visita,
          id: index + 1,
          aforo: index + 1,
          esNueva: false,
          servidorId: visita.id
        }));
        setVisitas(visitasExistentes);
        setVisitaActual(visitasExistentes[0]);
        setNextVisitaId(visitasExistentes.length + 1);
      }
    }
  }, [caso]);

  // =====================
  // Calendario de Visitas
  // =====================
  const initialDateForCalendar = new Date(obtenerFechaInicial() + 'T00:00:00');
  const [showCalendarioVisitas, setShowCalendarioVisitas] = useState(false);
  const [calendarYear, setCalendarYear] = useState(initialDateForCalendar.getFullYear());
  const [calendarMonth, setCalendarMonth] = useState(initialDateForCalendar.getMonth()); // 0-11
  const [fechaInicioCalendario, setFechaInicioCalendario] = useState(obtenerFechaInicial());
  const [diasSemanaSeleccionados, setDiasSemanaSeleccionados] = useState([obtenerDiaSemanaInicial()]);
  const [semanasSeleccionadas, setSemanasSeleccionadas] = useState(caso?.aforos?.[0]?.total_semana || 1);
  const [fechasPorDefecto, setFechasPorDefecto] = useState([]); // YYYY-MM-DD
  const [fechasSeleccionadas, setFechasSeleccionadas] = useState({}); // { 'YYYY-MM-DD': 'defecto'|'personalizada' }
  const [calendarError, setCalendarError] = useState('');

  const formatearYYYYMMDD = (dateObj) => {
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseYYYYMMDD = (str) => new Date(str + 'T12:00:00');

  const diasSemanaMapNombreANumero = {
    'domingo': 0, 'lunes': 1, 'martes': 2, 'miercoles': 3,
    'jueves': 4, 'viernes': 5, 'sabado': 6
  };

  const diasSemanaLabel = [
    { value: 'lunes', label: 'Lun' },
    { value: 'martes', label: 'Mar' },
    { value: 'miercoles', label: 'Mié' },
    { value: 'jueves', label: 'Jue' },
    { value: 'viernes', label: 'Vie' },
    { value: 'sabado', label: 'Sáb' },
    { value: 'domingo', label: 'Dom' }
  ];

  const calcularFechasPorDefecto = (fechaInicio, diasSeleccionados, cantidadSemanas) => {
    if (!fechaInicio || !diasSeleccionados || diasSeleccionados.length === 0) return [];
    const fechas = [];
    for (let semana = 0; semana < cantidadSemanas; semana++) {
      diasSeleccionados.forEach(diaNombre => {
        const diaNumero = diasSemanaMapNombreANumero[diaNombre];
        const fechaBase = new Date(fechaInicio + 'T12:00:00');
        const fechaBaseDay = fechaBase.getDay();
        let diasHasta = diaNumero - fechaBaseDay;
        if (diasHasta < 0) diasHasta += 7;
        const fechaVisita = new Date(fechaBase);
        fechaVisita.setDate(fechaBase.getDate() + diasHasta + (semana * 7));
        const fechaFormateada = formatearYYYYMMDD(fechaVisita);
        fechas.push(fechaFormateada);
      });
    }
    return Array.from(new Set(fechas)).sort((a, b) => parseYYYYMMDD(a) - parseYYYYMMDD(b));
  };

  const esFechaOcupada = (fechaStr) => {
    return visitas.some(v => v.fecha_visita === fechaStr);
  };

  const limitesSeleccion = () => {
    const limites = validarLimiteVisitas();
    const seleccionadasCount = Object.keys(fechasSeleccionadas).length;
    return {
      ...limites,
      totalSeleccionadas: seleccionadasCount,
      excede: visitas.length + seleccionadasCount > limites.maxVisitas
    };
  };

  const toggleFechaSeleccionada = (fechaStr, origen = 'personalizada') => {
    setCalendarError('');
    if (esFechaOcupada(fechaStr)) {
      setCalendarError(`La fecha ${fechaStr} ya tiene una visita programada.`);
      return;
    }
    setFechasSeleccionadas(prev => {
      const nuevo = { ...prev };
      if (nuevo[fechaStr]) {
        delete nuevo[fechaStr];
      } else {
        const lim = validarLimiteVisitas();
        const seleccionadasCount = Object.keys(prev).length;
        if (visitas.length + seleccionadasCount + 1 > lim.maxVisitas) {
          setCalendarError(`Se supera el límite máximo de ${lim.maxVisitas} visitas.`);
          return prev;
        }
        nuevo[fechaStr] = origen;
      }
      return nuevo;
    });
  };

  const preseleccionarDefecto = () => {
    const fechas = calcularFechasPorDefecto(fechaInicioCalendario, diasSemanaSeleccionados, semanasSeleccionadas);
    setFechasPorDefecto(fechas);
    setFechasSeleccionadas(prev => {
      const nuevo = { ...prev };
      const lim = validarLimiteVisitas();
      for (const f of fechas) {
        if (esFechaOcupada(f)) continue;
        if (!nuevo[f]) {
          const seleccionadasCount = Object.keys(nuevo).length;
          if (visitas.length + seleccionadasCount + 1 > lim.maxVisitas) {
            setCalendarError(`Se supera el límite máximo de ${lim.maxVisitas} al preseleccionar.`);
            break;
          }
          nuevo[f] = 'defecto';
        }
      }
      return nuevo;
    });
  };

  const limpiarSeleccionCalendario = () => {
    setFechasPorDefecto([]);
    setFechasSeleccionadas({});
    setCalendarError('');
  };

  // Validar y abrir el modal de calendario de visitas
  const abrirCalendarioVisitas = () => {
    // Validar que existan recipientes seleccionados con unidades antes de abrir el modal
    if (!visitaActual.recipientes || visitaActual.recipientes.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Recipientes requeridos',
        text: 'La visita actual debe tener al menos un recipiente antes de generar nuevas visitas.',
        confirmButtonColor: '#dc3545'
      });
      return;
    }
    const recipientesSinUnidades = visitaActual.recipientes.filter(r => !r.unidad || r.unidad <= 0);
    if (recipientesSinUnidades.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Unidades requeridas',
        text: 'La visita actual debe tener todos los recipientes con unidades especificadas.',
        confirmButtonColor: '#dc3545'
      });
      return;
    }

    // Sincronizar el mes/año del calendario con la fecha de inicio seleccionada
    try {
      const d = new Date(fechaInicioCalendario + 'T00:00:00');
      if (!isNaN(d.getTime())) {
        setCalendarYear(d.getFullYear());
        setCalendarMonth(d.getMonth());
      }
    } catch (_) {}

    setShowCalendarioVisitas(true);
  };

  const cambiarMes = (delta) => {
    const newMonth = calendarMonth + delta;
    if (newMonth < 0) {
      setCalendarMonth(11);
      setCalendarYear(calendarYear - 1);
    } else if (newMonth > 11) {
      setCalendarMonth(0);
      setCalendarYear(calendarYear + 1);
    } else {
      setCalendarMonth(newMonth);
    }
  };

  // Mantener el calendario sincronizado cuando cambia la fecha inicial
  useEffect(() => {
    if (!fechaInicioCalendario) return;
    try {
      const d = new Date(fechaInicioCalendario + 'T00:00:00');
      if (!isNaN(d.getTime())) {
        setCalendarYear(d.getFullYear());
        setCalendarMonth(d.getMonth());
      }
    } catch (_) {}
  }, [fechaInicioCalendario]);

  const getDaysMatrixForMonth = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const startDayMonday = (firstDay.getDay() + 6) % 7; // Lunes=0
    const startDate = new Date(year, month, 1 - startDayMonday);
    const days = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      const dateStr = formatearYYYYMMDD(d);
      days.push({
        dateObj: d,
        dateStr,
        inMonth: d.getMonth() === month,
        isDefault: fechasPorDefecto.includes(dateStr),
        isSelected: !!fechasSeleccionadas[dateStr],
        isConflict: esFechaOcupada(dateStr),
        isToday: formatearYYYYMMDD(new Date()) === dateStr
      });
    }
    return days;
  };

  const confirmarGeneracionVisitas = () => {
    // Validar que la primera visita tenga recipientes con unidades
    if (!visitaActual.recipientes || visitaActual.recipientes.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Recipientes requeridos',
        text: 'La visita actual debe tener al menos un recipiente antes de generar nuevas visitas.',
        confirmButtonColor: '#dc3545'
      });
      return;
    }
    const recipientesSinUnidades = visitaActual.recipientes.filter(r => !r.unidad || r.unidad <= 0);
    if (recipientesSinUnidades.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Unidades requeridas',
        text: 'La visita actual debe tener todos los recipientes con unidades especificadas.',
        confirmButtonColor: '#dc3545'
      });
      return;
    }

    const fechasFinales = Object.keys(fechasSeleccionadas)
      .filter(f => !esFechaOcupada(f))
      .sort((a, b) => parseYYYYMMDD(a) - parseYYYYMMDD(b));

    if (fechasFinales.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Sin fechas seleccionadas',
        text: 'Selecciona al menos una fecha en el calendario.',
        confirmButtonColor: '#17a2b8'
      });
      return;
    }

    const limites = validarLimiteVisitas();
    if (visitas.length + fechasFinales.length > limites.maxVisitas) {
      Swal.fire({
        icon: 'warning',
        title: 'Límite alcanzado',
        text: `No se pueden generar más visitas. Límite máximo: ${limites.maxVisitas} visitas (${limites.totalFrecuencia} frecuencias × ${limites.totalSemana} semanas).`,
        confirmButtonColor: '#dc3545'
      });
      return;
    }

    // Guardar la visita actual antes de generar nuevas
    const visitasActualizadas = [...visitas];
    visitasActualizadas[visitaActualIndex] = { ...visitaActual };

    const firmasGuardadas = obtenerFirmasDeVisitasGuardadas();
    let nextIdLocal = nextVisitaId;
    let creadas = 0;

    fechasFinales.forEach(fechaFormateada => {
      const diaNombre = obtenerDiaSemanaDeFecha(fechaFormateada);
      const nuevaVisita = {
        id: nextIdLocal,
        aforo: nextIdLocal,
        fecha_visita: fechaFormateada,
        hora_visita: obtenerHoraActual(),
        dia_semana: diaNombre,
        observaciones: visitaActual.observaciones || '',
        nombre_representante: firmasGuardadas.nombre_representante || '',
        firma_representante: firmasGuardadas.firma_representante || null,
        recipientes: visitaActual.recipientes ? visitaActual.recipientes.map((r, index) => ({
          ...r,
          unidad: r.unidad || 1,
          id: r.id || Date.now() + Math.random() + index,
          parametrizacion_recipiente: r.parametrizacion_recipiente ? { ...r.parametrizacion_recipiente } : undefined
        })) : [],
        esNueva: true,
        servidorId: null,
        tipo_generacion: fechasSeleccionadas[fechaFormateada] || 'personalizada'
      };
      visitasActualizadas.push(nuevaVisita);
      creadas++;
      nextIdLocal++;
    });

    setVisitas(visitasActualizadas);
    setNextVisitaId(nextIdLocal);
    setShowCalendarioVisitas(false);
    limpiarSeleccionCalendario();

    Swal.fire({
      icon: 'success',
      title: '¡Visitas generadas!',
      text: `Se han creado ${creadas} nueva(s) visita(s).`,
      confirmButtonColor: '#28a745'
    });
  };

  // Función para guardar visita
  const guardarVisita = async () => {
    try {
      // Validar campos requeridos
      if (!visitaActual.recipientes || visitaActual.recipientes.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Campo requerido',
          text: 'Por favor agrega al menos un recipiente',
          confirmButtonColor: '#dc3545'
        });
        return;
      }

      // Validación especial para la primera visita: debe tener recipientes con unidades
      const esPrimeraVisita = visitaActualIndex === 0;
      if (esPrimeraVisita) {
        const recipientesSinUnidades = visitaActual.recipientes.filter(recipiente => 
          !recipiente.unidad || recipiente.unidad <= 0
        );
        
        if (recipientesSinUnidades.length > 0) {
          Swal.fire({
            icon: 'warning',
            title: 'Unidades requeridas',
            text: 'En la primera visita, todos los recipientes deben tener unidades especificadas (mayor a 0)',
            confirmButtonColor: '#dc3545'
          });
          return;
        }
      }

      if (!visitaActual.fecha_visita) {
        Swal.fire({
          icon: 'warning',
          title: 'Campo requerido',
          text: 'Por favor selecciona la fecha de visita',
          confirmButtonColor: '#dc3545'
        });
        return;
      }

      if (!visitaActual.hora_visita) {
        Swal.fire({
          icon: 'warning',
          title: 'Campo requerido',
          text: 'Por favor selecciona la hora de visita',
          confirmButtonColor: '#dc3545'
        });
        return;
      }

      // Validar que no existan visitas duplicadas en la misma fecha
      const validacionFecha = validarVisitaDuplicada(
        visitaActual.fecha_visita, 
        visitaActual.dia_semana, 
        visitaActual.id
      );
      
      if (!validacionFecha.esValida) {
        Swal.fire({
          icon: 'warning',
          title: 'Fecha duplicada',
          text: validacionFecha.mensaje,
          confirmButtonColor: '#dc3545'
        });
        return;
      }

      Swal.fire({
        title: 'Guardando visita...',
        text: 'Por favor espera mientras se guarda la información',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Preparar datos para enviar usando FormData (como en ActaVisita.js)
      const formData = new FormData();
      
      const aforoId = caso?.aforos?.[0]?.id || visitaActual.aforo;
      
      // Agregar campos básicos
      formData.append('caso_id', caso.id);
      formData.append('aforo', aforoId);
      formData.append('fecha_visita', visitaActual.fecha_visita);
      formData.append('hora_visita', visitaActual.hora_visita);
      formData.append('dia_semana', visitaActual.dia_semana || '');
      formData.append('observaciones', visitaActual.observaciones || '');
      formData.append('nombre_representante', visitaActual.nombre_representante || '');
      
      // Formatear recipientes según el formato requerido
      const recipientesFormateados = visitaActual.recipientes.map(recipiente => {
        // Usar parametrizacion_recipiente.id como tipo_recipiente si existe
        const tipoRecipiente = recipiente.parametrizacion_recipiente?.id || recipiente.tipo_recipiente;
        return {
          tipo_recipiente: tipoRecipiente,
          unidad: recipiente.unidad
        };
      });
      
      formData.append('recipientes', JSON.stringify(recipientesFormateados));
      
      // Manejar firmas - usar las firmas temporales si están disponibles, sino las de la visita actual
      const firmaRepresentante = tempRepresentanteFirma || visitaActual.firma_representante;
      
      // Agregar archivos de firma si existen
      if (firmaRepresentante instanceof File) {
        formData.append('firma_representante', firmaRepresentante);
        console.log('✍️ Agregando firma representante como archivo');
      } else if (typeof firmaRepresentante === 'string' && firmaRepresentante) {
        // Si es una URL de firma existente, enviarla como string
        formData.append('firma_representante_url', firmaRepresentante);
        console.log('✍️ Agregando firma representante como URL');
      }

      // Log de datos enviados
      console.log('📤 DATOS ENVIADOS:', {
        caso_id: caso.id,
        aforo: aforoId,
        fecha_visita: visitaActual.fecha_visita,
        hora_visita: visitaActual.hora_visita,
        recipientes: recipientesFormateados,
        observaciones: visitaActual.observaciones || '',
        nombre_representante: visitaActual.nombre_representante || ''
      });

      const response = await postVisitaAforo(formData);
      
      // Log de respuesta del servidor
      console.log('📥 RESPUESTA DEL SERVIDOR:', response);
      
      Swal.close();
      
      if (response && response.data) {
        // Actualizar la visita con los datos del servidor
        const visitaGuardada = {
          ...visitaActual,
          esNueva: false,
          servidorId: response.data.id
        };
        
        // Actualizar en el array de visitas
        const visitasActualizadas = [...visitas];
        visitasActualizadas[visitaActualIndex] = visitaGuardada;
        setVisitas(visitasActualizadas);
        setVisitaActual(visitaGuardada);
        
        Swal.fire({
          icon: 'success',
          title: '¡Visita guardada!',
          text: 'La visita se ha guardado correctamente en el sistema',
          confirmButtonColor: '#28a745'
        });
        
        // Llamar al callback de éxito para refrescar datos
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error('Respuesta inválida del servidor');
      }
    } catch (error) {
      console.error('❌ ERROR:', error.response?.data || error.message);
      
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Error al guardar',
        text: `No se pudo guardar la visita: ${error.response?.data?.message || error.message}`,
        confirmButtonColor: '#dc3545'
      });
    }
  };

  // Función para guardar todas las visitas
  const guardarTodasLasVisitas = async () => {
    try {
      const visitasPendientes = visitas.filter(visita => visita.esNueva);
      
      if (visitasPendientes.length === 0) {
        Swal.fire({
          icon: 'info',
          title: 'Sin cambios',
          text: 'No hay visitas pendientes por guardar',
          confirmButtonColor: '#17a2b8'
        });
        return;
      }

      Swal.fire({
        title: `Guardando ${visitasPendientes.length} visita(s)...`,
        text: 'Por favor espera mientras se guardan todas las visitas',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const promesasGuardado = visitasPendientes.map(async (visita, index) => {
        // Preparar datos usando FormData como en guardarVisita
        const formData = new FormData();
        
        const aforoId = caso?.aforos?.[0]?.id || visita.aforo;
        
        // Agregar campos básicos
        formData.append('caso_id', caso.id);
        formData.append('aforo', aforoId);
        formData.append('fecha_visita', visita.fecha_visita);
        formData.append('hora_visita', visita.hora_visita);
        formData.append('dia_semana', visita.dia_semana || '');
        formData.append('observaciones', visita.observaciones || '');
        formData.append('nombre_representante', visita.nombre_representante || '');
        
        // Formatear recipientes según el formato requerido
        console.log(`🚀 Recipientes originales de visita ${visita.aforo}:`, visita.recipientes);
        const recipientesFormateados = visita.recipientes.map((recipiente, index) => {
          console.log(`📦 Procesando recipiente ${index}:`, recipiente);
          // Usar parametrizacion_recipiente.id como tipo_recipiente si existe
          const tipoRecipiente = recipiente.parametrizacion_recipiente?.id || recipiente.tipo_recipiente;
          const formateado = {
            tipo_recipiente: tipoRecipiente,
            unidad: recipiente.unidad
          };
          console.log(`✅ Recipiente formateado ${index}:`, formateado);
          return formateado;
        });
        
        console.log(`📋 Recipientes finales para envío:`, recipientesFormateados);
        formData.append('recipientes', JSON.stringify(recipientesFormateados));
        
        // Manejar firmas - usar las firmas temporales si están disponibles, sino las de la visita
        const firmaRepresentante = tempRepresentanteFirma || visita.firma_representante;
        
        // Agregar archivos de firma si existen
        if (firmaRepresentante instanceof File) {
          formData.append('firma_representante', firmaRepresentante);
          console.log(`✍️ Agregando firma representante como archivo para visita ${visita.aforo}`);
        } else if (typeof firmaRepresentante === 'string' && firmaRepresentante) {
          // Si es una URL de firma existente, enviarla como string
          formData.append('firma_representante_url', firmaRepresentante);
          console.log(`✍️ Agregando firma representante como URL para visita ${visita.aforo}`);
        }
        
        // Log de datos enviados para cada visita
        console.log(`📤 DATOS ENVIADOS para visita ${visita.aforo}:`, {
          caso_id: caso.id,
          aforo: aforoId,
          fecha_visita: visita.fecha_visita,
          hora_visita: visita.hora_visita,
          recipientes: recipientesFormateados,
          observaciones: visita.observaciones || '',
          nombre_representante: visita.nombre_representante || ''
        });
        
        return await postVisitaAforo(formData);
      });

      const resultados = await Promise.all(promesasGuardado);
      
      // Actualizar todas las visitas con los datos del servidor
      const visitasActualizadas = visitas.map(visita => {
        if (visita.esNueva) {
          const resultado = resultados.find(r => r.data);
          if (resultado) {
            return {
              ...visita,
              esNueva: false,
              servidorId: resultado.data.id
            };
          }
        }
        return visita;
      });
      
      setVisitas(visitasActualizadas);
      
      Swal.close();
      Swal.fire({
        icon: 'success',
        title: '¡Visitas guardadas!',
        text: `Se han guardado ${visitasPendientes.length} visita(s) correctamente`,
        confirmButtonColor: '#28a745'
      });
      
      // Llamar al callback de éxito para refrescar datos
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error al guardar visitas:', error);
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Error al guardar',
        text: 'No se pudieron guardar todas las visitas. Por favor intenta nuevamente.',
        confirmButtonColor: '#dc3545'
      });
    }
  };

  // Función para obtener firmas de visitas ya guardadas
  const obtenerFirmasDeVisitasGuardadas = () => {
    console.log('🔍 Obteniendo firmas para nuevas visitas...');
    console.log('🔍 Firmas temporales disponibles:', {
      tempRepresentanteFirma: tempRepresentanteFirma ? 'Sí' : 'No'
    });
    
    // Primero, intentar usar las firmas temporales si existen
    if (tempRepresentanteFirma) {
      console.log('✅ Usando firmas temporales para nuevas visitas');
      return {
        firma_representante: tempRepresentanteFirma || visitaActual.firma_representante,
        nombre_representante: visitaActual.nombre_representante
      };
    }
    
    // Si no hay firmas temporales, usar las de la visita actual
    if (visitaActual.firma_representante) {
      console.log('✅ Usando firmas de la visita actual para nuevas visitas');
      return {
        firma_representante: visitaActual.firma_representante,
        nombre_representante: visitaActual.nombre_representante
      };
    }
    
    // Como último recurso, buscar en visitas ya guardadas
    const visitaConFirmas = visitas.find(visita => 
      !visita.esNueva && 
      visita.firma_representante
    );
    
    if (visitaConFirmas) {
      console.log('✅ Usando firmas de visita guardada para nuevas visitas');
      return {
        firma_representante: visitaConFirmas.firma_representante,
        nombre_representante: visitaConFirmas.nombre_representante
      };
    }
    
    // Si no hay firmas disponibles, retornar valores vacíos
    console.log('⚠️ No se encontraron firmas, usando valores vacíos');
    return {
      firma_representante: null,
      nombre_representante: visitaActual.nombre_representante || ''
    };
  };

  // Función para generar múltiples visitas consecutivas
  const generarVisitasConsecutivas = () => {
    // Obtener configuración del aforo
    const total_frecuencia = caso?.aforos?.[0]?.total_frecuencia || 7;
    const total_semana = caso?.aforos?.[0]?.total_semana || 1;
    
    // Validar límites antes de generar visitas
    const limites = validarLimiteVisitas();
    if (!limites.puedeAgregarMas) {
      Swal.fire({
        icon: 'warning',
        title: 'Límite alcanzado',
        text: `No se pueden generar más visitas. Límite máximo: ${limites.maxVisitas} visitas (${limites.totalFrecuencia} frecuencias × ${limites.totalSemana} semanas).`,
        confirmButtonColor: '#dc3545'
      });
      return;
    }
    
    // Validar que la visita actual tenga recipientes con unidades
    if (!visitaActual.recipientes || visitaActual.recipientes.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Recipientes requeridos',
        text: 'La visita actual debe tener al menos un recipiente antes de generar nuevas visitas.',
        confirmButtonColor: '#dc3545'
      });
      return;
    }
    
    const recipientesSinUnidades = visitaActual.recipientes.filter(recipiente => 
      !recipiente.unidad || recipiente.unidad <= 0
    );
    
    if (recipientesSinUnidades.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Unidades requeridas',
        text: 'La visita actual debe tener todos los recipientes con unidades especificadas.',
        confirmButtonColor: '#dc3545'
      });
      return;
    }
    
    // Obtener fecha del informe como fecha mínima, o fecha actual como fallback
    const fechaMinima = obtenerFechaInicial();
    const fechaInicioSugerida = fechaMinima;
    
    // Obtener días de la semana disponibles
    const diasSemanaOptions = [
      { value: 'lunes', label: 'Lunes' },
      { value: 'martes', label: 'Martes' },
      { value: 'miercoles', label: 'Miércoles' },
      { value: 'jueves', label: 'Jueves' },
      { value: 'viernes', label: 'Viernes' },
      { value: 'sabado', label: 'Sábado' },
      { value: 'domingo', label: 'Domingo' }
    ];
    
    const checkboxOptions = diasSemanaOptions.map(dia => 
      `<div class="form-check">
        <input class="form-check-input" type="checkbox" value="${dia.value}" id="dia_${dia.value}">
        <label class="form-check-label" for="dia_${dia.value}">${dia.label}</label>
      </div>`
    ).join('');
    
    Swal.fire({
      title: 'Generar Visitas Consecutivas',
      html: `
        <div class="mb-3">
          <label class="form-label fw-bold">Fecha de inicio:</label>
          <input type="date" id="fechaInicio" class="form-control" value="${fechaInicioSugerida}" min="${fechaMinima}">
          <small class="text-muted">Las visitas se generarán desde esta fecha (mínimo: fecha del informe)</small>
        </div>
        <div class="mb-3">
          <label class="form-label fw-bold">Selecciona los días de la semana:</label>
          <div class="mt-2">
            ${checkboxOptions}
          </div>
          <small class="text-muted">Se generará una visita para cada día seleccionado</small>
        </div>
        <div class="mb-3">
          <label class="form-label fw-bold">¿Cuántas semanas generar?</label>
          <input type="number" id="cantidadSemanas" class="form-control" value="1" min="1" max="${Math.min(4, total_semana || 4)}" oninput="actualizarLimiteSemanas()">
          <small id="semanasHelp" class="text-muted">Se repetirán los días seleccionados durante estas semanas (máximo permitido: ${total_semana || 4})</small>
          <div id="warningMessage" class="alert alert-warning mt-2" style="display: none;"></div>
        </div>
        <div class="alert alert-info">
          <strong>Límites configurados:</strong><br>
          • Frecuencia máxima: ${total_frecuencia || 'No definida'} días por semana<br>
          • Semanas máximas: ${total_semana || 'No definidas'}<br>
          • Visitas máximas totales: ${limites.maxVisitas}<br>
          • Visitas ya generadas: ${limites.visitasActuales}<br>
          • Visitas disponibles para generar: ${limites.visitasRestantes}
        </div>
        <script>
          function actualizarLimiteSemanas() {
            const diasSeleccionados = document.querySelectorAll('input[type="checkbox"]:checked').length;
            const cantidadSemanas = parseInt(document.getElementById('cantidadSemanas').value) || 0;
            const visitasRestantes = ${limites.visitasRestantes};
            const totalSemana = ${total_semana || 4};
            
            const totalVisitasAGenerar = diasSeleccionados * cantidadSemanas;
            const maxSemanasPermitidas = diasSeleccionados > 0 ? Math.floor(visitasRestantes / diasSeleccionados) : 0;
            const maxSemanasReal = Math.min(maxSemanasPermitidas, totalSemana);
            
            const warningDiv = document.getElementById('warningMessage');
            const semanasInput = document.getElementById('cantidadSemanas');
            const helpText = document.getElementById('semanasHelp');
            
            if (totalVisitasAGenerar > visitasRestantes) {
              warningDiv.style.display = 'block';
              warningDiv.innerHTML = \`⚠️ <strong>Límite excedido:</strong><br>
              • Visitas a generar: \${totalVisitasAGenerar} (\${diasSeleccionados} días × \${cantidadSemanas} semanas)<br>
              • Visitas disponibles: \${visitasRestantes}<br>
              • Máximo de semanas permitidas: \${maxSemanasReal}\`;
              semanasInput.style.borderColor = '#dc3545';
            } else {
              warningDiv.style.display = 'none';
              semanasInput.style.borderColor = '';
            }
            
            // Actualizar el máximo dinámicamente
            semanasInput.max = maxSemanasReal;
            helpText.innerHTML = \`Se repetirán los días seleccionados durante estas semanas (máximo permitido: \${Math.min(maxSemanasReal, totalSemana)})\`;
          }
          
          // Agregar event listeners a los checkboxes
          document.addEventListener('change', function(e) {
            if (e.target.type === 'checkbox') {
              actualizarLimiteSemanas();
            }
          });
          
          // Inicializar
          setTimeout(actualizarLimiteSemanas, 100);
        </script>
      `,
      showCancelButton: true,
      confirmButtonColor: '#17a2b8',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Generar Visitas',
      cancelButtonText: 'Cancelar',
      width: '500px',
      preConfirm: () => {
        const fechaInicio = document.getElementById('fechaInicio').value;
        const cantidadSemanas = parseInt(document.getElementById('cantidadSemanas').value);
        const diasSeleccionados = [];
        
        diasSemanaOptions.forEach(dia => {
          const checkbox = document.getElementById(`dia_${dia.value}`);
          if (checkbox.checked) {
            diasSeleccionados.push(dia.value);
          }
        });
        
        if (!fechaInicio) {
          Swal.showValidationMessage('Por favor selecciona una fecha de inicio');
          return false;
        }
        
        // Validar que la fecha de inicio no sea anterior a la fecha mínima
        if (fechaInicio < fechaMinima) {
          Swal.showValidationMessage(`La fecha de inicio no puede ser anterior a la fecha del informe (${fechaMinima})`);
          return false;
        }
        
        if (diasSeleccionados.length === 0) {
          Swal.showValidationMessage('Por favor selecciona al menos un día de la semana');
          return false;
        }
        
        // Calcular el máximo de semanas permitidas basado en días seleccionados y visitas restantes
        const maxSemanasPermitidas = diasSeleccionados.length > 0 ? Math.floor(limites.visitasRestantes / diasSeleccionados.length) : 0;
        const maxSemanasReal = Math.min(maxSemanasPermitidas, total_semana || 4);
        
        if (cantidadSemanas < 1 || cantidadSemanas > maxSemanasReal) {
          Swal.showValidationMessage(`La cantidad de semanas debe estar entre 1 y ${maxSemanasReal}. Con ${diasSeleccionados.length} días seleccionados, solo puedes generar máximo ${maxSemanasReal} semanas`);
          return false;
        }
        
        // Validar límite de frecuencia (días por semana)
        if (total_frecuencia && diasSeleccionados.length > total_frecuencia) {
          Swal.showValidationMessage(`No puedes seleccionar más de ${total_frecuencia} días por semana según la configuración del aforo`);
          return false;
        }
        
        // Calcular total de visitas que se van a generar
        const totalVisitasAGenerar = diasSeleccionados.length * cantidadSemanas;
        
        // Validar que no se exceda el límite total de visitas
        if (limites.visitasRestantes < totalVisitasAGenerar) {
          Swal.showValidationMessage(`No se pueden generar ${totalVisitasAGenerar} visitas (${diasSeleccionados.length} días × ${cantidadSemanas} semanas). Solo quedan ${limites.visitasRestantes} visitas disponibles de un total de ${limites.maxVisitas} permitidas.`);
          return false;
        }
        
        return { fechaInicio, diasSeleccionados, cantidadSemanas };
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const { fechaInicio, diasSeleccionados, cantidadSemanas } = result.value;
        
        // Guardar la visita actual antes de generar nuevas
        const visitasActualizadas = [...visitas];
        visitasActualizadas[visitaActualIndex] = { ...visitaActual };
        setVisitas(visitasActualizadas);
        
        const nuevasVisitas = [...visitasActualizadas];
        const diasSemanaMap = {
          'domingo': 0, 'lunes': 1, 'martes': 2, 'miercoles': 3,
          'jueves': 4, 'viernes': 5, 'sabado': 6
        };
        
        let visitasCreadas = 0;
        let nextId = nextVisitaId;
        
        // Generar visitas para cada semana
        for (let semana = 0; semana < cantidadSemanas; semana++) {
          // Para cada día seleccionado
          diasSeleccionados.forEach(diaNombre => {
            const diaNumero = diasSemanaMap[diaNombre];
            
            // Calcular la fecha para este día en esta semana
            const fechaBase = new Date(fechaInicio + 'T12:00:00'); // Agregar hora para evitar problemas de zona horaria
            const fechaBaseDay = fechaBase.getDay();
            
            console.log(`🗓️ Calculando fecha para ${diaNombre} (${diaNumero}) en semana ${semana}`);
            console.log(`📅 Fecha base: ${fechaInicio}, día de la semana: ${fechaBaseDay}`);
            
            // Calcular días hasta el día objetivo
            let diasHasta = diaNumero - fechaBaseDay;
            
            // Si el día ya pasó en la semana actual (pero no si es el mismo día), ir a la siguiente semana
            if (diasHasta < 0) {
              diasHasta += 7;
            }
            
            // Agregar días de la semana actual más las semanas adicionales
            const fechaVisita = new Date(fechaBase);
            fechaVisita.setDate(fechaBase.getDate() + diasHasta + (semana * 7));
            
            console.log(`⏰ Días hasta ${diaNombre}: ${diasHasta}, semanas adicionales: ${semana * 7}`);
            console.log(`📆 Fecha calculada: ${fechaVisita.toDateString()}`);
            
            // Formatear fecha manualmente para evitar problemas de zona horaria
            const year = fechaVisita.getFullYear();
            const month = String(fechaVisita.getMonth() + 1).padStart(2, '0');
            const day = String(fechaVisita.getDate()).padStart(2, '0');
            const fechaFormateada = `${year}-${month}-${day}`;
            
            // Validar que no exista duplicado
            const existeDuplicado = nuevasVisitas.some(v => v.fecha_visita === fechaFormateada);
            if (existeDuplicado) {
              return; // Saltar esta fecha si ya existe
            }
            
            // Obtener firmas de visitas ya guardadas
            const firmasGuardadas = obtenerFirmasDeVisitasGuardadas();
            
            // Crear nueva visita heredando toda la información de la visita actual
            const nuevaVisita = {
              id: nextId,
              aforo: nextId,
              fecha_visita: fechaFormateada,
              hora_visita: obtenerHoraActual(),
              dia_semana: diaNombre,
              observaciones: visitaActual.observaciones || '',
              nombre_representante: firmasGuardadas.nombre_representante || '',
              firma_representante: firmasGuardadas.firma_representante || null,
              recipientes: visitaActual.recipientes ? visitaActual.recipientes.map((r, index) => {
                console.log(`🔍 Recipiente original ${index}:`, r);
                const recipienteCopia = {
                  ...r, // Preservar toda la estructura original
                  unidad: r.unidad || 1,
                  id: r.id || Date.now() + Math.random() + index,
                  // Asegurar que parametrizacion_recipiente se preserve correctamente
                  parametrizacion_recipiente: r.parametrizacion_recipiente ? {
                    ...r.parametrizacion_recipiente
                  } : undefined
                };
                console.log(`📋 Recipiente copiado ${index}:`, recipienteCopia);
                return recipienteCopia;
              }) : [],
              esNueva: true,
              servidorId: null
            };
            
            nuevasVisitas.push(nuevaVisita);
            visitasCreadas++;
            nextId++;
          });
        }
        
        setVisitas(nuevasVisitas);
        setNextVisitaId(nextId);
        
        Swal.fire({
          icon: 'success',
          title: '¡Visitas generadas!',
          text: `Se han creado ${visitasCreadas} nueva(s) visita(s) con los recipientes de la primera visita`,
          confirmButtonColor: '#28a745'
        });
      }
    });
  };

  // Funciones para gestionar múltiples visitas
  const agregarNuevaVisita = () => {
    // Validar límites antes de agregar
    const limites = validarLimiteVisitas();
    if (!limites.puedeAgregarMas) {
      Swal.fire({
        icon: 'warning',
        title: 'Límite alcanzado',
        text: `No se pueden agregar más visitas. Límite máximo: ${limites.maxVisitas} visitas (${limites.totalFrecuencia} frecuencias × ${limites.totalSemana} semanas).`,
        confirmButtonColor: '#dc3545'
      });
      return;
    }
    
    // Validar que la primera visita tenga recipientes con unidades antes de crear nuevas visitas
    if (visitas.length > 0) {
      const primeraVisita = visitas[0];
      if (!primeraVisita.recipientes || primeraVisita.recipientes.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Recipientes requeridos',
          text: 'La primera visita debe tener al menos un recipiente antes de poder crear nuevas visitas.',
          confirmButtonColor: '#dc3545'
        });
        return;
      }
      
      // Validar que todos los recipientes de la primera visita tengan unidades
      const recipientesSinUnidades = primeraVisita.recipientes.filter(recipiente => 
        !recipiente.unidad || recipiente.unidad <= 0
      );
      
      if (recipientesSinUnidades.length > 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Unidades requeridas',
          text: 'La primera visita debe tener todos los recipientes con unidades especificadas antes de crear nuevas visitas.',
          confirmButtonColor: '#dc3545'
        });
        return;
      }
    }
    
    // Guardar la visita actual antes de crear una nueva
    const visitasActualizadas = [...visitas];
    visitasActualizadas[visitaActualIndex] = { ...visitaActual };
    setVisitas(visitasActualizadas);
    
    // Preguntar al usuario sobre los recipientes para la nueva visita
    Swal.fire({
      title: '¿Qué recipientes usar?',
      text: '¿Deseas usar los mismos recipientes de la primera visita o empezar con recipientes nuevos?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#17a2b8',
      confirmButtonText: 'Usar los mismos',
      cancelButtonText: 'Recipientes nuevos',
      reverseButtons: true
    }).then((result) => {
      const nuevaVisita = crearVisitaVacia(nextVisitaId);
      
      // Validar que la nueva fecha no esté duplicada
      const validacionFecha = validarVisitaDuplicada(
        nuevaVisita.fecha_visita, 
        nuevaVisita.dia_semana, 
        null
      );
      
      if (!validacionFecha.esValida) {
        Swal.fire({
          icon: 'warning',
          title: 'Fecha duplicada',
          text: validacionFecha.mensaje + ' Por favor, selecciona una fecha diferente para la nueva visita.',
          confirmButtonColor: '#dc3545'
        });
        return;
      }
      
      // Obtener firmas de visitas ya guardadas
      const firmasGuardadas = obtenerFirmasDeVisitasGuardadas();
      
      // Heredar algunos datos de la primera visita y firmas guardadas
      if (visitasActualizadas.length > 0) {
        const primeraVisita = visitasActualizadas[0];
        nuevaVisita.nombre_representante = firmasGuardadas.nombre_representante;
        nuevaVisita.firma_representante = firmasGuardadas.firma_representante;
        
        // Heredar recipientes según la elección del usuario
        if (result.isConfirmed) {
          // Usar los mismos recipientes de la primera visita
          nuevaVisita.recipientes = primeraVisita.recipientes ? [...primeraVisita.recipientes] : [];
        } else {
          // Empezar con recipientes vacíos
          nuevaVisita.recipientes = [];
        }
      }
      
      const nuevasVisitas = [...visitasActualizadas, nuevaVisita];
      setVisitas(nuevasVisitas);
      setNextVisitaId(prev => prev + 1);
      setVisitaActualIndex(nuevasVisitas.length - 1);
      setVisitaActual(nuevaVisita);
    });
  };

  // Función para obtener información de configuración del aforo
  const getConfiguracionAforo = () => {
    const totalFrecuencia = caso?.aforos?.[0]?.total_frecuencia || 7;
    const totalSemana = caso?.aforos?.[0]?.total_semana || 1;
    const maxVisitas = totalFrecuencia * totalSemana;
    
    return {
      totalFrecuencia,
      totalSemana,
      maxVisitas,
      visitasActuales: visitas.length,
      visitasRestantes: Math.max(0, maxVisitas - visitas.length)
    };
  };

  const eliminarVisita = (index) => {
    if (visitas.length <= 1) {
      Swal.fire({
        icon: 'warning',
        title: 'No se puede eliminar',
        text: 'Debe existir al menos una visita',
        confirmButtonColor: '#dc3545'
      });
      return;
    }

    const visitaAEliminar = visitas[index];
    const esVisitaNueva = visitaAEliminar.esNueva || !visitaAEliminar.servidorId;
    const tipoEliminacion = esVisitaNueva ? 'de la vista' : 'del sistema';
    
    Swal.fire({
      title: '¿Estás seguro?',
      text: `Esta acción eliminará la visita ${tipoEliminacion} y no se puede deshacer`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        const nuevasVisitas = visitas.filter((_, i) => i !== index);
        setVisitas(nuevasVisitas);
        
        // Ajustar índice actual
        if (index === visitaActualIndex) {
          setVisitaActualIndex(0);
          setVisitaActual(nuevasVisitas[0]);
        } else if (index < visitaActualIndex) {
          setVisitaActualIndex(prev => prev - 1);
        }
        
        Swal.fire({
          icon: 'success',
          title: 'Visita Eliminada',
          text: `La visita ha sido eliminada ${tipoEliminacion} correctamente`,
          confirmButtonColor: '#28a745'
        });
      }
    });
  };

  const cambiarVisita = (index) => {
    if (index >= 0 && index < visitas.length) {
      // Guardar cambios de la visita actual incluyendo firmas temporales
      const visitasActualizadas = [...visitas];
      const visitaConFirmasActualizadas = {
        ...visitaActual,
        // Asegurar que las firmas temporales se preserven
        firma_representante: tempRepresentanteFirma || visitaActual.firma_representante
      };
      visitasActualizadas[visitaActualIndex] = visitaConFirmasActualizadas;
      setVisitas(visitasActualizadas);
      
      // Limpiar firmas temporales antes del cambio
      setTempRepresentanteFirma(null);
      
      // Cambiar a la visita seleccionada
      setVisitaActualIndex(index);
      setVisitaActual(visitasActualizadas[index]);
    }
  };

  const actualizarVisitaActual = (nuevosValores) => {
    // Si se está actualizando la fecha o día de semana, validar duplicados
    if (nuevosValores.fecha_visita || nuevosValores.dia_semana) {
      const fechaAValidar = nuevosValores.fecha_visita || visitaActual.fecha_visita;
      const diaAValidar = nuevosValores.dia_semana || visitaActual.dia_semana;
      
      const validacionFecha = validarVisitaDuplicada(
        fechaAValidar,
        diaAValidar,
        visitaActual.id
      );
      
      if (!validacionFecha.esValida) {
        Swal.fire({
          icon: 'warning',
          title: 'Fecha duplicada',
          text: validacionFecha.mensaje,
          confirmButtonColor: '#dc3545'
        });
        return false; // Indicar que la actualización falló
      }
    }
    
    // Agregar logs de depuración para recipientes
    if (nuevosValores.recipientes) {
      console.log('🔄 Actualizando recipientes en visitaActual:', nuevosValores.recipientes);
      console.log('📋 Recipientes actuales antes del cambio:', visitaActual.recipientes);
    }
    
    // Agregar logs de depuración para firmas
    if (nuevosValores.firma_representante !== undefined || nuevosValores.firma_visitador !== undefined) {
      console.log('✍️ Actualizando firmas en visitaActual:', {
        firma_representante: nuevosValores.firma_representante
      });
    }
    
    const visitaActualizada = { ...visitaActual, ...nuevosValores };
    
    if (nuevosValores.recipientes) {
      console.log('✅ Recipientes después de la actualización:', visitaActualizada.recipientes);
    }
    
    if (nuevosValores.firma_representante !== undefined) {
      console.log('✅ Firmas después de la actualización:', {
        firma_representante: visitaActualizada.firma_representante
      });
    }
    
    setVisitaActual(visitaActualizada);
    
    // Actualizar en el array de visitas
    const visitasActualizadas = [...visitas];
    visitasActualizadas[visitaActualIndex] = visitaActualizada;
    setVisitas(visitasActualizadas);
    
    return true; // Indicar que la actualización fue exitosa
  };

  // Funciones para manejo de firmas
  const handleRepresentanteSignatureSave = (signatureFile) => {
    setTempRepresentanteFirma(signatureFile);
    actualizarVisitaActual({ firma_representante: signatureFile });
    setShowRepresentanteSignature(false);
    console.log('✍️ Firma de representante guardada:', signatureFile?.name);
  };

  const clearRepresentanteSignature = () => {
    setTempRepresentanteFirma(null);
    actualizarVisitaActual({ firma_representante: null });
    console.log('🗑️ Firma de representante eliminada');
  };

  // Función para generar PDF
  const generarPDF = async () => {
    try {
      Swal.fire({
        title: 'Generando PDF...',
        text: 'Por favor espera mientras se genera el documento',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const result = await openAforoPDF(visitas, caso);
      
      Swal.close();
      
      if (!result.success) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo generar el PDF. Por favor intenta nuevamente.',
          confirmButtonColor: '#dc3545'
        });
      }
    } catch (error) {
      console.error('Error al generar PDF:', error);
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Ocurrió un error al generar el PDF. Por favor intenta nuevamente.',
        confirmButtonColor: '#dc3545'
      });
    }
  };

  // Función para descargar PDF
  const descargarPDF = async () => {
    try {
      Swal.fire({
        title: 'Generando PDF...',
        text: 'Por favor espera mientras se genera el documento para descarga',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const result = await generateAforoPDF(visitas, caso);
      
      Swal.close();
      
      if (result.success) {
        Swal.fire({
          icon: 'success',
          title: '¡Descarga exitosa!',
          text: 'El PDF se ha descargado correctamente',
          confirmButtonColor: '#28a745'
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo generar el PDF para descarga. Por favor intenta nuevamente.',
          confirmButtonColor: '#dc3545'
        });
      }
    } catch (error) {
      console.error('Error al descargar PDF:', error);
      Swal.close();
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Ocurrió un error al generar el PDF. Por favor intenta nuevamente.',
        confirmButtonColor: '#dc3545'
      });
    }
  };


  return (
    <div className="container-fluid">
      {/* Header Principal Corporativo */}
      <div className="mb-4">
        <div className="d-flex align-items-center mb-4">

          <div className="flex-grow-1">

          </div>
          <div className="d-flex gap-2">
            {(() => {
              // Verificar si hay visitas nuevas sin guardar
              const visitasNuevasSinGuardar = visitas.filter(v => v.esNueva && !v.servidorId);
              const cantidadVisitasNuevas = visitasNuevasSinGuardar.length;
              
              // Si no hay visitas nuevas, no mostrar botón
              if (cantidadVisitasNuevas === 0) {
                return null;
              }
              
              // Si hay múltiples visitas nuevas (generadas), mostrar "Guardar Todas"
              if (cantidadVisitasNuevas > 1) {
                return (
                  <Button
                    color="info"
                    className="d-flex align-items-center"
                    onClick={guardarTodasLasVisitas}
                    disabled={isCasoFinalizado}
                    style={{
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontWeight: '500',
                      fontSize: '0.95rem',
                      boxShadow: '0 4px 12px rgba(23, 162, 184, 0.3)'
                    }}
                  >
                    <i className="fas fa-save me-2"></i>
                    Guardar Todas
                  </Button>
                );
              }
              
              // Si hay una sola visita nueva, mostrar "Guardar Visita"
              if (cantidadVisitasNuevas === 1) {
                return (
                  <Button
                    color="warning"
                    className="d-flex align-items-center"
                    onClick={guardarVisita}
                    disabled={isCasoFinalizado}
                    style={{
                      borderRadius: '8px',
                      padding: '12px 24px',
                      fontWeight: '500',
                      fontSize: '0.95rem',
                      boxShadow: '0 4px 12px rgba(255, 193, 7, 0.3)'
                    }}
                  >
                    <i className="fas fa-save me-2"></i>
                    Guardar Visita
                  </Button>
                );
              }
              
              return null;
            })()}
            <Button
              color="success"
              className="d-flex align-items-center"
              onClick={generarPDF}
              disabled={isCasoFinalizado}
              style={{
                borderRadius: '8px',
                padding: '12px 24px',
                fontWeight: '500',
                fontSize: '0.95rem',
                boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)'
              }}
            >
              <i className="fas fa-eye me-2"></i>
              Ver PDF
            </Button>
            <Button
              color="primary"
              className="d-flex align-items-center"
              onClick={descargarPDF}
              disabled={isCasoFinalizado}
              style={{
                borderRadius: '8px',
                padding: '12px 24px',
                fontWeight: '500',
                fontSize: '0.95rem',
                boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)'
              }}
            >
              <i className="fas fa-download me-2"></i>
              Descargar PDF
            </Button>
          </div>
        </div>

        {/* Información del Caso */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '12px' }}>
          <CardBody className="p-4">
            <Row className="g-4">
              <Col md={4}>
                <div className="d-flex align-items-center">
                  <div>
                    <h6 className="mb-1 fw-semibold">Cuenta Contrato</h6>
                    <p className="mb-0 text-muted">{caso?.aforos?.[0].cuenta_contrato || 'No asignada'}</p>
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div className="d-flex align-items-center">
                  <div className="bg-info bg-opacity-10 rounded-circle p-3 me-3">
                    <i className="fas fa-ticket-alt text-info"></i>
                  </div>
                  <div>
                    <h6 className="mb-1 fw-semibold">Número PQR</h6>
                    <p className="mb-0 text-muted">{caso?.aforos?.[0]?.numero_pqr || 'No asignado'}</p>
                  </div>
                </div>
              </Col>
              <Col md={4}>
                <div className="d-flex align-items-center">

                  <div>
                    <h6 className="mb-1 fw-semibold">Total Visitas</h6>
                    <p className="mb-0">
                      <Badge color="success" className="px-2 py-1" style={{ fontSize: '0.9rem' }}>{visitas.length}</Badge>
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </div>

      {/* Gestión de Visitas Múltiples */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <div style={{
              backgroundColor: '#17a2b8',
              borderRadius: '8px',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '12px'
            }}>
              <i className="fas fa-calendar-alt text-white"></i>
            </div>
            <h5 className="fw-bold mb-0" style={{ color: '#17a2b8', fontSize: '1.25rem' }}>Gestión de Visitas</h5>
          </div>
          {!isCasoFinalizado && (
            <div className="d-flex gap-2">
              <Button
                color="success"
                className="d-flex align-items-center"
                onClick={agregarNuevaVisita}
                style={{
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontWeight: '500',
                  fontSize: '0.9rem',
                  boxShadow: '0 3px 10px rgba(40, 167, 69, 0.3)'
                }}
              >
                <i className="fas fa-plus me-2"></i>
                Nueva Visita
              </Button>
              <Button
                color="info"
                className="d-flex align-items-center"
                onClick={abrirCalendarioVisitas}
                style={{
                  borderRadius: '8px',
                  padding: '10px 20px',
                  fontWeight: '500',
                  fontSize: '0.9rem',
                  boxShadow: '0 3px 10px rgba(23, 162, 184, 0.3)'
                }}
              >
                <i className="fas fa-calendar-plus me-2"></i>
                Generar Visitas
              </Button>
            </div>
          )}
        </div>
        {/* Modal Calendario de Visitas */}
        <Modal isOpen={showCalendarioVisitas} toggle={() => setShowCalendarioVisitas(false)} size="lg">
          <ModalHeader toggle={() => setShowCalendarioVisitas(false)}>Generar visitas con calendario</ModalHeader>
          <ModalBody>
            <Row>
              <Col md={5} className="mb-3">
                <div className="mb-3">
                  <Label className="fw-bold">Fecha de inicio</Label>
                  <Input type="date" value={fechaInicioCalendario} onChange={(e) => setFechaInicioCalendario(e.target.value)} />
                  <small className="text-muted">Mínimo: fecha del informe</small>
                </div>
                <div className="mb-3">
                  <Label className="fw-bold">Días de la semana por defecto</Label>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    {diasSemanaLabel.map(d => (
                      <div key={d.value} className="form-check">
                        <Input
                          className="form-check-input"
                          type="checkbox"
                          checked={diasSemanaSeleccionados.includes(d.value)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setDiasSemanaSeleccionados(prev => {
                              if (checked) return Array.from(new Set([...prev, d.value]));
                              return prev.filter(x => x !== d.value);
                            });
                          }}
                        />
                        <Label className="form-check-label">{d.label}</Label>
                      </div>
                    ))}
                  </div>
                  <small className="text-muted">Se generará una visita para cada día seleccionado por semana.</small>
                </div>
                <div className="mb-3">
                  <Label className="fw-bold">Semanas</Label>
                  <Input type="number" min={1} max={Math.max(1, caso?.aforos?.[0]?.total_semana || 4)} value={semanasSeleccionadas}
                    onChange={(e) => setSemanasSeleccionadas(Math.max(1, parseInt(e.target.value || '1')))} />
                </div>
                <div className="d-flex gap-2 mb-3">
                  <Button color="info" onClick={preseleccionarDefecto} className="d-flex align-items-center">
                    <i className="fas fa-magic me-2"></i>Preseleccionar por defecto
                  </Button>
                  <Button color="secondary" onClick={limpiarSeleccionCalendario} className="d-flex align-items-center">
                    <i className="fas fa-eraser me-2"></i>Limpiar selección
                  </Button>
                </div>
                <div>
                  <h6 className="fw-semibold">Fechas por defecto</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {fechasPorDefecto.map(f => (
                      <Badge key={f} color="info" className="px-2 py-1">{f}</Badge>
                    ))}
                    {fechasPorDefecto.length === 0 && (
                      <span className="text-muted">Aún sin preseleccionar</span>
                    )}
                  </div>
                </div>
              </Col>
              <Col md={7}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <Button color="light" onClick={() => cambiarMes(-1)}><i className="fas fa-chevron-left"></i></Button>
                  <h6 className="mb-0 fw-bold">{new Date(calendarYear, calendarMonth).toLocaleString('es-ES', { month: 'long', year: 'numeric' })}</h6>
                  <Button color="light" onClick={() => cambiarMes(1)}><i className="fas fa-chevron-right"></i></Button>
                </div>
                <div className="d-grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px' }}>
                  {diasSemanaLabel.map(d => (
                    <div key={d.value} className="text-center fw-semibold text-muted" style={{ fontSize: '0.85rem' }}>{d.label}</div>
                  ))}
                  {getDaysMatrixForMonth(calendarYear, calendarMonth).map(day => (
                    <div
                      key={day.dateStr}
                      className="p-2 text-center"
                      style={{
                        borderRadius: '8px',
                        cursor: day.inMonth ? 'pointer' : 'default',
                        backgroundColor: day.isSelected ? '#d4edda' : (day.isDefault ? '#e7f5ff' : '#fff'),
                        border: day.isConflict ? '2px solid #dc3545' : '1px solid #dee2e6',
                        opacity: day.inMonth ? 1 : 0.5
                      }}
                      onClick={() => day.inMonth && toggleFechaSeleccionada(day.dateStr, day.isDefault ? 'defecto' : 'personalizada')}
                    >
                      <div className="fw-bold" style={{ fontSize: '0.9rem' }}>{day.dateObj.getDate()}</div>
                      {day.isConflict && (
                        <div className="text-danger" style={{ fontSize: '0.75rem' }}>Ocupada</div>
                      )}
                      {day.isSelected && (
                        <div className="text-success" style={{ fontSize: '0.75rem' }}>{fechasSeleccionadas[day.dateStr] === 'defecto' ? 'Defecto' : 'Pers.'}</div>
                      )}
                    </div>
                  ))}
                </div>
                {calendarError && (
                  <div className="alert alert-warning mt-3 mb-0">{calendarError}</div>
                )}
                <div className="mt-3">
                  <h6 className="fw-semibold">Fechas seleccionadas</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {Object.keys(fechasSeleccionadas).map(f => (
                      <Badge key={f} color={fechasSeleccionadas[f] === 'defecto' ? 'info' : 'success'} className="px-2 py-1">
                        {f} {fechasSeleccionadas[f] === 'defecto' ? '(defecto)' : '(personalizada)'}
                      </Badge>
                    ))}
                    {Object.keys(fechasSeleccionadas).length === 0 && (
                      <span className="text-muted">Sin selección</span>
                    )}
                  </div>
                </div>
                <div className="mt-2">
                  {(() => {
                    const lim = limitesSeleccion();
                    return (
                      <div className="text-muted">
                        <span>Seleccionadas: {lim.totalSeleccionadas} · Existentes: {lim.visitasActuales} · Máximo: {lim.maxVisitas}</span>
                        {lim.excede && <div className="text-danger">Se supera el límite establecido.</div>}
                      </div>
                    );
                  })()}
                </div>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowCalendarioVisitas(false)}>Cancelar</Button>
            <Button color="primary" onClick={confirmarGeneracionVisitas} disabled={Object.keys(fechasSeleccionadas).length === 0}>Generar</Button>
          </ModalFooter>
        </Modal>

        {/* Navegación de Visitas Mejorada */}
        <div className="d-flex flex-wrap gap-2 mb-3">
          {visitas.map((visita, index) => (
              <div 
                key={visita.id} 
                className="position-relative"
                onMouseEnter={() => setHoveredVisitIndex(index)}
                onMouseLeave={() => setHoveredVisitIndex(null)}
              >
                <Button
                  color={index === visitaActualIndex ? 'primary' : 'outline-primary'}
                  className="d-flex align-items-center position-relative"
                  onClick={() => {
                    cambiarVisita(index);
                  }}
                  style={{
                    borderRadius: '10px',
                    padding: '10px 16px',
                    fontWeight: '500',
                    fontSize: '0.9rem',
                    minWidth: '120px',
                    boxShadow: index === visitaActualIndex ? '0 4px 12px rgba(0, 123, 255, 0.3)' : 'none',
                    border: visita.esNueva ? '2px dashed #ffc107' : '1px solid #dee2e6'
                  }}
                >
                  <i className={`fas ${visita.esNueva ? 'fa-clock' : 'fa-check-circle'} me-2`} 
                     style={{ color: visita.esNueva ? '#ffc107' : '#28a745' }}></i>
                  <div className="text-start">
                    <div className="fw-bold">Visita {visita.aforo}</div>
                    {visita.dia_semana && (
                      <small className="text-muted">
                        {visita.dia_semana}
                        {visita.fecha_visita && (
                          <span className="ms-1 fw-bold text-info">
                            {(() => {
                              // Evitar problemas de zona horaria construyendo la fecha directamente
                              const [año, mes, dia] = visita.fecha_visita.split('-');
                              if (año && mes && dia) {
                                const fecha = new Date(año, mes - 1, dia); // mes-1 porque Date usa 0-11
                                if (!isNaN(fecha.getTime())) {
                                  return fecha.getDate();
                                }
                              }
                              return visita.fecha_visita.split('-')[2] || ''; // Fallback: extraer día directamente
                            })()}
                          </span>
                        )}
                      </small>
                    )}
                  </div>
                  {/* Badge de estado */}
                  <Badge 
                    color={visita.esNueva ? 'warning' : 'success'} 
                    className="position-absolute top-0 start-0 translate-middle"
                    style={{ fontSize: '0.7rem' }}
                    title={visita.esNueva ? 'Pendiente de guardar' : 'Guardada'}
                  >
                    {visita.esNueva ? 'P' : '✓'}
                  </Badge>
                </Button>
                {/* Botón de eliminar en la esquina superior derecha con hover */}
                {!isCasoFinalizado && visitas.length > 1 && (
                  <Button
                    color="danger"
                    size="sm"
                    className="position-absolute"
                    onClick={(e) => {
                      e.stopPropagation();
                      eliminarVisita(index);
                    }}
                    style={{
                      top: '-8px',
                      right: '-8px',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      padding: '0',
                      fontSize: '0.7rem',
                      opacity: hoveredVisitIndex === index ? '1' : '0',
                      transition: 'opacity 0.2s ease-in-out',
                      zIndex: 10,
                      border: '2px solid white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                      pointerEvents: hoveredVisitIndex === index ? 'auto' : 'none'
                    }}
                    title="Eliminar visita"
                  >
                    <i className="fas fa-times"></i>
                  </Button>
                )}
              </div>
          ))}
        </div>
        
        {/* Información de la Visita Actual */}
        <div className={`alert ${visitaActual.esNueva ? 'alert-warning border-warning' : 'alert-success border-success'} d-flex align-items-center justify-content-between`}>
          <div className="d-flex align-items-center">
            <i className={`fas ${visitaActual.esNueva ? 'fa-exclamation-triangle' : 'fa-check-circle'} me-2`}></i>
            <span>
              <strong>Visita Actual:</strong> Visita {visitaActual.aforo}
              {visitaActual.dia_semana && (
                <Badge color="info" className="ms-2">
                  {visitaActual.dia_semana}
                </Badge>
              )}
              {visitaActual.fecha_visita && (
                <Badge color="secondary" className="ms-2">
                  {(() => {
                    // Evitar problemas de zona horaria construyendo la fecha directamente
                    const [año, mes, dia] = visitaActual.fecha_visita.split('-');
                    if (año && mes && dia) {
                      const fecha = new Date(año, mes - 1, dia); // mes-1 porque Date usa 0-11
                      if (!isNaN(fecha.getTime())) {
                        return fecha.toLocaleDateString('es-CO');
                      }
                    }
                    return visitaActual.fecha_visita; // Fallback si no se puede parsear
                  })()}
                </Badge>
              )}
              <Badge 
                color={visitaActual.esNueva ? 'warning' : 'success'} 
                className="ms-2"
              >
                {visitaActual.esNueva ? 'Pendiente de guardar' : 'Guardada'}
              </Badge>
            </span>
          </div>
          {/* Contador de visitas pendientes */}
          <div className="d-flex align-items-center">
            {(() => {
              const visitasPendientes = visitas.filter(v => v.esNueva).length;
              return visitasPendientes > 0 ? (
                <Badge color="warning" className="d-flex align-items-center">
                  <i className="fas fa-clock me-1"></i>
                  {visitasPendientes} pendiente{visitasPendientes > 1 ? 's' : ''}
                </Badge>
              ) : (
                <Badge color="success" className="d-flex align-items-center">
                  <i className="fas fa-check me-1"></i>
                  Todas guardadas
                </Badge>
              );
            })()} 
          </div>
        </div>
      </div>

      {/* Formulario de Datos de la Visita con Pestañas */}
      <div className="mb-4">
        {/* Sistema de Pestañas */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
          <CardBody className="p-0">
            {/* Navegación de Pestañas */}
            <Nav tabs className="border-0" style={{ backgroundColor: '#f8f9fa', borderRadius: '12px 12px 0 0' }}>
              <NavItem>
                <NavLink
                  className={`${activeTab === 'recipientes' ? 'active' : ''} fw-semibold`}
                  onClick={() => setActiveTab('recipientes')}
                  style={{
                    cursor: 'pointer',
                    borderRadius: '12px 0 0 0',
                    backgroundColor: activeTab === 'recipientes' ? '#fff' : 'transparent',
                    borderColor: activeTab === 'recipientes' ? '#dee2e6' : 'transparent',
                    color: activeTab === 'recipientes' ? '#495057' : '#6c757d'
                  }}
                >
                  <i className="fas fa-box me-2"></i>
                  Recipientes
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={`${activeTab === 'representantes' ? 'active' : ''} fw-semibold`}
                  onClick={() => setActiveTab('representantes')}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: activeTab === 'representantes' ? '#fff' : 'transparent',
                    borderColor: activeTab === 'representantes' ? '#dee2e6' : 'transparent',
                    color: activeTab === 'representantes' ? '#495057' : '#6c757d'
                  }}
                >
                  <i className="fas fa-users me-2"></i>
                  Representantes y Firmas
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={`${activeTab === 'informacion' ? 'active' : ''} fw-semibold`}
                  onClick={() => setActiveTab('informacion')}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: activeTab === 'informacion' ? '#fff' : 'transparent',
                    borderColor: activeTab === 'informacion' ? '#dee2e6' : 'transparent',
                    color: activeTab === 'informacion' ? '#495057' : '#6c757d'
                  }}
                >
                  <i className="fas fa-info-circle me-2"></i>
                  Información General
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={`${activeTab === 'resumenes' ? 'active' : ''} fw-semibold`}
                  onClick={() => setActiveTab('resumenes')}
                  style={{
                    cursor: 'pointer',
                    borderRadius: '0 12px 0 0',
                    backgroundColor: activeTab === 'resumenes' ? '#fff' : 'transparent',
                    borderColor: activeTab === 'resumenes' ? '#dee2e6' : 'transparent',
                    color: activeTab === 'resumenes' ? '#495057' : '#6c757d'
                  }}
                >
                  <i className="fas fa-chart-line me-2"></i>
                  Resúmenes
                </NavLink>
              </NavItem>
            </Nav>

            {/* Contenido de las Pestañas */}
            <TabContent activeTab={activeTab} className="p-4">
              {/* Pestaña de Recipientes */}
              <TabPane tabId="recipientes">
                <div className="mb-4">
                  <h5 className="fw-bold mb-3 text-success">
                    <i className="fas fa-box me-2"></i>
                    Gestión de Recipientes
                  </h5>
                  
                  <RecipientesManager
                    recipientes={visitaActual.recipientes || []}
                    onChange={(nuevosRecipientes) => actualizarVisitaActual({ recipientes: nuevosRecipientes })}
                    opcionesRecipientes={opcionesRecipientes}
                    disabled={isCasoFinalizado}
                    restrictMode={false}
                    volumenTotalVisita={visitaActual.volumen_total_visita}
                  />
                </div>
              </TabPane>

              {/* Pestaña de Representantes y Firmas */}
              <TabPane tabId="representantes">
                <div className="mb-4">
                  <h5 className="fw-bold mb-3 text-danger">
                    <i className="fas fa-users me-2"></i>
                    Información de Representantes
                  </h5>
                  
                  <Row className="g-3">
                    <Col md={6}>
                      <FormGroup className="mb-3">
                        <Label for="nombre_representante" className="fw-semibold text-dark mb-2" style={{ fontSize: '0.95rem' }}>
                          <i className="fas fa-user me-2 text-danger"></i>
                          Nombre del Representante *
                        </Label>
                        <Input
                          type="text"
                          id="nombre_representante"
                          value={visitaActual.nombre_representante || ''}
                          onChange={(e) => actualizarVisitaActual({ nombre_representante: e.target.value })}
                          placeholder="Ingrese el nombre del representante"
                          disabled={isCasoFinalizado}
                          className="form-control"
                          style={{ borderRadius: '8px' }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>

                {/* Sección de Firmas */}
                <div className="mb-4">
                  <h5 className="fw-bold mb-3 text-info">
                    <i className="fas fa-signature me-2"></i>
                    Firmas Digitales
                  </h5>
                  
                  <Row className="g-4">
                    <Col md={12}>
                      <FormGroup className="mb-3">
                        <Label className="fw-semibold text-dark mb-3" style={{ fontSize: '1rem' }}>
                          <i className="fas fa-pen-fancy me-2 text-teal"></i>
                          Firma del Representante
                        </Label>
                        <div 
                          className="signature-section p-4 border rounded bg-light" 
                          style={{ 
                            minHeight: '200px',
                            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
                            borderRadius: '12px'
                          }}
                        >
                          {(tempRepresentanteFirma || visitaActual.firma_representante) ? (
                            <div className="text-center">
                              <img
                                src={
                                  tempRepresentanteFirma 
                                    ? (tempRepresentanteFirma instanceof File ? URL.createObjectURL(tempRepresentanteFirma) : tempRepresentanteFirma)
                                    : (visitaActual.firma_representante instanceof File ? URL.createObjectURL(visitaActual.firma_representante) : getBaseUrl() + visitaActual.firma_representante)
                                }
                                alt="Firma Representante"
                                style={{ maxWidth: '200px', maxHeight: '120px', border: '2px solid #ddd', borderRadius: '8px' }}
                              />
                              <div className="mt-3">
                                {!isCasoFinalizado && (
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={clearRepresentanteSignature}
                                    style={{ borderRadius: '8px', fontSize: '0.9rem', padding: '8px 16px' }}
                                  >
                                    <i className="fas fa-trash me-2"></i>
                                    Limpiar Firma
                                  </Button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-center text-muted d-flex flex-column align-items-center justify-content-center h-100">
                              <i className="fas fa-signature mb-3" style={{ fontSize: '2.5rem' }}></i>
                              <h6 className="mb-3">Sin firma registrada</h6>
                              {!isCasoFinalizado && (
                                <Button
                                  color="info"
                                  size="lg"
                                  onClick={() => setShowRepresentanteSignature(true)}
                                  style={{ borderRadius: '8px', fontSize: '1rem', padding: '12px 24px' }}
                                >
                                  <i className="fas fa-pen me-2"></i>
                                  Agregar Firma
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
                </TabPane>

                {/* Pestaña de Información General */}
                <TabPane tabId="informacion">
                  <div className="mb-4">
                    <h5 className="fw-bold mb-3 text-primary">
                      <i className="fas fa-info-circle me-2"></i>
                      Información General de la Visita
                    </h5>
                    
                    {/* Sección de Observaciones Generales */}
                    <div className="mb-3">
                      <FormGroup className="mb-2">
                        <Label for="observaciones" className="fw-semibold text-dark mb-2" style={{ fontSize: '0.95rem' }}>
                          <i className="fas fa-edit me-2 text-purple"></i>
                          Observaciones de la Visita
                        </Label>
                        <Input
                          type="textarea"
                          id="observaciones"
                          rows="4"
                          value={visitaActual.observaciones || ''}
                          onChange={(e) => actualizarVisitaActual({ observaciones: e.target.value })}
                          placeholder="Escriba aquí las observaciones de la visita..."
                          disabled={isCasoFinalizado}
                          className="form-control"
                          style={{ borderRadius: '8px' }}
                        />
                      </FormGroup>
                    </div>
                  </div>
                </TabPane>

                {/* Pestaña de Resúmenes */}
                <TabPane tabId="resumenes">
                  <div className="mb-4">
                    <h5 className="fw-bold mb-3 text-success">
                      <i className="fas fa-chart-line me-2"></i>
                      Resúmenes Informativos
                    </h5>
                    
                    {/* Resumen de Visitas - Equilibrado */}
                    {visitas.length > 1 && (
                      <div className="mb-4">
                        <div className="d-flex align-items-center mb-3">
                          <div style={{
                            backgroundColor: '#6f42c1',
                            borderRadius: '8px',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '12px'
                          }}>
                            <i className="fas fa-list text-white"></i>
                          </div>
                          <h6 className="fw-bold mb-0" style={{ color: '#6f42c1' }}>
                            Resumen de Visitas ({visitas.length}/{getConfiguracionAforo().maxVisitas})
                          </h6>
                          <small className="text-muted">
                            Configuración: {getConfiguracionAforo().totalFrecuencia} frecuencias × {getConfiguracionAforo().totalSemana} semanas
                            {getConfiguracionAforo().visitasRestantes > 0 && (
                              <span className="text-success"> • {getConfiguracionAforo().visitasRestantes} visitas restantes</span>
                            )}
                            {getConfiguracionAforo().visitasRestantes === 0 && (
                              <span className="text-warning"> • Límite alcanzado</span>
                            )}
                          </small>
                        </div>
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                          <CardBody className="p-0">
                            <div className="table-responsive">
                              <Table className="mb-0" style={{ borderRadius: '10px', overflow: 'hidden' }}>
                                <thead style={{ backgroundColor: '#6f42c1', color: 'white' }}>
                                  <tr>
                                    <th className="border-0 py-3 px-3 fw-semibold">Visita</th>
                                    <th className="border-0 py-3 px-3 fw-semibold">Fecha</th>
                                    <th className="border-0 py-3 px-3 fw-semibold">Hora</th>
                                    <th className="border-0 py-3 px-3 fw-semibold">Día</th>
                                    <th className="border-0 py-3 px-3 fw-semibold">Recipientes</th>
                                    <th className="border-0 py-3 px-3 fw-semibold">Representante</th>
                                    <th className="border-0 py-3 px-3 fw-semibold text-center">Firmas</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {visitas.map((visita, index) => (
                                    <tr 
                                      key={visita.id} 
                                      className={index === visitaActualIndex ? 'table-active' : ''}
                                      style={{
                                        backgroundColor: index === visitaActualIndex ? '#e3f2fd' : 'transparent',
                                        borderLeft: index === visitaActualIndex ? '2px solid #2196f3' : 'none'
                                      }}
                                    >
                                      <td className="py-2 px-3">
                                        <Badge 
                                          color={index === visitaActualIndex ? 'primary' : 'secondary'}
                                          className="px-2 py-1"
                                          style={{ borderRadius: '6px', fontWeight: '500' }}
                                        >
                                          <i className="fas fa-calendar-day me-1"></i>
                                          {visita.aforo}
                                        </Badge>
                                      </td>
                                      <td className="py-2 px-3">
                                        {visita.fecha_visita ? (
                                          <div className="d-flex align-items-center gap-2">
                                            <span className="fw-medium">
                                              {(() => {
                                                // Evitar problemas de zona horaria construyendo la fecha directamente
                                                const [año, mes, dia] = visita.fecha_visita.split('-');
                                                if (año && mes && dia) {
                                                  const fecha = new Date(año, mes - 1, dia); // mes-1 porque Date usa 0-11
                                                  if (!isNaN(fecha.getTime())) {
                                                    return fecha.toLocaleDateString('es-CO');
                                                  }
                                                }
                                                return visita.fecha_visita; // Fallback si no se puede parsear
                                              })()}
                                            </span>
                                            {visita.tipo_generacion && (
                                              <Badge color={visita.tipo_generacion === 'defecto' ? 'info' : 'success'} className="px-2 py-1" style={{ borderRadius: '5px', fontSize: '0.75rem' }}>
                                                {visita.tipo_generacion === 'defecto' ? 'Defecto' : 'Personalizada'}
                                              </Badge>
                                            )}
                                          </div>
                                        ) : (
                                          <span className="text-muted">-</span>
                                        )}
                                      </td>
                                      <td className="py-2 px-3">
                                        {visita.hora_visita ? (
                                          <Badge color="info" className="px-2 py-1" style={{ borderRadius: '5px', fontSize: '0.85rem', fontWeight: '500' }}>
                                            <i className="fas fa-clock me-1"></i>
                                            {visita.hora_visita}
                                          </Badge>
                                        ) : (
                                          <span className="text-muted">-</span>
                                        )}
                                      </td>
                                      <td className="py-2 px-3">
                                        {visita.dia_semana ? (
                                          <Badge color="warning" className="px-2 py-1" style={{ borderRadius: '5px', fontSize: '0.85rem', fontWeight: '500', color: '#000' }}>
                                            {visita.dia_semana}
                                          </Badge>
                                        ) : (
                                          <span className="text-muted">-</span>
                                        )}
                                      </td>
                                      <td className="py-1 px-2">
                                        {visita.recipientes && visita.recipientes.length > 0 ? (
                                          <div className="d-flex flex-wrap gap-1">
                                            {visita.recipientes.map((recipiente, idx) => {
                                              // Obtener la descripción del recipiente desde parametrizacion_recipiente
                                              const descripcionRecipiente = recipiente.parametrizacion_recipiente?.descripcion || recipiente.tipo_recipiente || 'Sin tipo';
                                              return (
                                                <Badge 
                                                  key={idx} 
                                                  color="success" 
                                                  className="px-2 py-1" 
                                                  style={{ borderRadius: '4px', fontSize: '0.75rem', fontWeight: '500', marginBottom: '2px' }}
                                                  title={`${descripcionRecipiente}: ${recipiente.unidad} unidades`}
                                                >
                                                  <i className="fas fa-box me-1" style={{ fontSize: '0.7rem' }}></i>
                                                  {descripcionRecipiente} ({recipiente.unidad})
                                                </Badge>
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <small className="text-muted">Sin recipientes</small>
                                        )}
                                      </td>
                                      <td className="py-1 px-2">
                                        {visita.nombre_representante ? (
                                          <div className="d-flex align-items-center">
                                            <i className="fas fa-user me-1 text-primary" style={{ fontSize: '0.7em' }}></i>
                                            <small className="fw-medium" style={{ fontSize: '0.75em' }}>
                                              {visita.nombre_representante.length > 12 
                                                ? `${visita.nombre_representante.substring(0, 12)}...` 
                                                : visita.nombre_representante
                                              }
                                            </small>
                                          </div>
                                        ) : (
                                          <small className="text-muted">-</small>
                                        )}
                                      </td>
                                      <td className="py-1 px-2 text-center">
                                        <div className="d-flex justify-content-center gap-2">
                                          <div className="d-flex align-items-center">
                                            <Badge 
                                              color={visita.firma_representante ? 'success' : 'light'} 
                                              className="px-2 py-1 d-flex align-items-center"
                                              style={{ borderRadius: '6px', fontSize: '0.75em' }}
                                              title={visita.firma_representante ? 'Firma del representante registrada' : 'Sin firma del representante'}
                                            >
                                              <i className={`fas ${visita.firma_representante ? 'fa-check' : 'fa-times'} me-1`}></i>
                                              R
                                            </Badge>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </CardBody>
                        </Card>
                      </div>
                    )}

                    {/* Resumen General del Aforo */}
                    {caso?.aforos && caso.aforos.length > 0 && (
                      <div className="mb-4">
                        <div className="d-flex align-items-center mb-3">
                          <div style={{
                            backgroundColor: '#28a745',
                            borderRadius: '8px',
                            width: '36px',
                            height: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginRight: '12px'
                          }}>
                            <i className="fas fa-chart-bar text-white"></i>
                          </div>
                          <h6 className="fw-bold mb-0" style={{ color: '#28a745' }}>
                            Resumen General del Aforo
                          </h6>
                        </div>
                        <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                          <CardBody className="p-4">
                            <Row className="g-4">
                              {/* Totales Generales */}
                              <Col md={6}>
                                <div className="bg-light rounded p-3">
                                  <h6 className="fw-bold mb-3 text-primary">
                                    <i className="fas fa-calculator me-2"></i>
                                    Totales Generales
                                  </h6>
                                  <div className="row g-2">
                                    <div className="col-6">
                                      <div className="text-center p-2 bg-white rounded">
                                        <div className="fw-bold text-primary" style={{ fontSize: '1.2rem' }}>
                                          {caso.aforos[0].total_visitas || 0}
                                        </div>
                                        <small className="text-muted">Total Visitas</small>
                                      </div>
                                    </div>
                                    <div className="col-6">
                                      <div className="text-center p-2 bg-white rounded">
                                        <div className="fw-bold text-success" style={{ fontSize: '1.2rem' }}>
                                          {caso.aforos[0].total_recipientes_aforo || 0}
                                        </div>
                                        <small className="text-muted">Total Recipientes</small>
                                      </div>
                                    </div>
                                    <div className="col-6">
                                      <div className="text-center p-2 bg-white rounded">
                                        <div className="fw-bold text-info" style={{ fontSize: '1.2rem' }}>
                                          {caso.aforos[0].total_unidades_aforo || 0}
                                        </div>
                                        <small className="text-muted">Total Unidades</small>
                                      </div>
                                    </div>
                                    <div className="col-6">
                                      <div className="text-center p-2 bg-white rounded">
                                        <div className="fw-bold text-warning" style={{ fontSize: '1.2rem' }}>
                                          {caso.aforos[0].numero_semanas || 0}
                                        </div>
                                        <small className="text-muted">Semanas</small>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </Col>
                              
                              {/* Volúmenes */}
                              <Col md={6}>
                                <div className="bg-light rounded p-3">
                                  <h6 className="fw-bold mb-3 text-warning">
                                    <i className="fas fa-cube me-2"></i>
                                    Análisis de Volúmenes
                                  </h6>
                                  <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center p-2 bg-white rounded mb-2">
                                      <span className="fw-medium">Volumen Total:</span>
                                      <Badge color="primary" className="px-3 py-2">
                                        {caso.aforos[0].volumen_total_aforo || 0} m³
                                      </Badge>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center p-2 bg-white rounded mb-2">
                                      <span className="fw-medium">Promedio Semanal:</span>
                                      <Badge color="info" className="px-3 py-2">
                                        {caso.aforos[0].volumen_promedio_semanal || 0} m³
                                      </Badge>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center p-2 bg-white rounded">
                                      <span className="fw-medium">Factor Total:</span>
                                      <Badge color="success" className="px-3 py-2">
                                        {caso.aforos[0].valor_total_factor || 0}
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </CardBody>
                        </Card>
                      </div>
                    )}
                  </div>
                </TabPane>
              </TabContent>
          </CardBody>
        </Card>
      </div>


        
        {/* Modales de firma */}
        <SignatureModal
        isOpen={showRepresentanteSignature}
        onClose={() => setShowRepresentanteSignature(false)}
        onSave={handleRepresentanteSignatureSave}
        title="Firma del Representante"
      />
      </div>
  );
};

export default AforoForm;
